
import { useCallback, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { fetchContentItem } from "./fetchContentItem";

/**
 * Handles navigation between content items with prefetching
 */
export const useContentNavigation = (currentId: string | undefined) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [navigationInProgress, setNavigationInProgress] = useState(false);
  const navigationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleRelatedContentClick = useCallback((clickedId: string) => {
    if (!clickedId) {
      console.error("[NAVIGATION DEBUG] Attempt to navigate to content with null/undefined ID");
      return;
    }
    
    if (clickedId === currentId) {
      console.log("[NAVIGATION DEBUG] Already on this content item, ignoring click");
      return;
    }
    
    if (navigationInProgress) {
      console.log("[NAVIGATION DEBUG] Navigation already in progress, ignoring click");
      return;
    }
    
    console.log("[NAVIGATION DEBUG] FocusedContent - Navigating to related content:", clickedId, "from current content:", currentId);
    setNavigationInProgress(true);
    
    queryClient.prefetchQuery({
      queryKey: ["contentItem", clickedId],
      queryFn: async () => {
        if (!user) throw new Error("User not authenticated");
        return fetchContentItem(clickedId, user.id, queryClient);
      }
    }).then(() => {
      console.log("[NAVIGATION DEBUG] Successfully prefetched data for:", clickedId);
    }).catch(err => {
      console.error("[NAVIGATION DEBUG] Failed to prefetch data:", err);
    }).finally(() => {
      console.log("[NAVIGATION DEBUG] Executing navigation to:", `/content/${clickedId}`);
      navigate(`/content/${clickedId}`);
      
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
      
      navigationTimeoutRef.current = setTimeout(() => {
        setNavigationInProgress(false);
        console.log("[NAVIGATION DEBUG] Navigation completed, reset navigationInProgress");
      }, 1000);
    });
  }, [currentId, navigate, navigationInProgress, queryClient, user]);

  return {
    navigationInProgress,
    handleRelatedContentClick
  };
};
